import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { queriesPayeeRows, queriesPayees } from "@query";
import { selectorPayeeDisplayName } from "../../../../shared/selectors/selectorPayeeDisplayName";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";

type Value = string | null;

type Props = {
  value: Value;
  onChange: (value: Value) => void;
} & Omit<WSSelectSearchProps, "options" | "value" | "onChange">;

export const SelectPayee: React.FC<Props> = ({
  value,
  onChange,
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const queryPayeeRows = queriesPayeeRows.searchPayeeRows.usePaginatedQuery({
    filter: {
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  const xWingspanAccount = useXWingspanPayerId();
  const queryPayee = queriesPayees.getPayee.useQuery(
    { payeeId: value as string, xWingspanAccount },
    { enabled: !!value }
  );

  const payee = queryPayee.data;

  useEffect(() => {
    if (!payee) {
      if (value) {
        return;
      }

      setSelectValue(null);
      return;
    }

    const name = selectorPayeeDisplayName(payee);
    const secondaryName = payee.email;

    setSelectValue({
      value: payee.id!,
      label: name,
      labels: [secondaryName].filter(Boolean) as string[],
      avatar: {
        type: "text",
        text: name
      }
    });
  }, [payee, value]);

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayeeRows.flatData ?? []).forEach(payee => {
      const name = selectorSearchRowName(payee);

      options.push({
        value: payee.id ?? "",
        label: name,
        labels: payee.email ? [payee.email] : [],
        avatar: {
          type: "text",
          text: name
        }
      });
    });

    return options;
  }, [queryPayeeRows.flatData]);

  const onReachMenuEnd = useCallback(() => {
    if (queryPayeeRows.hasNextPage) {
      queryPayeeRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayeeRows.hasNextPage]);

  return (
    <WSSelectSearch
      {...otherProps}
      value={selectValue}
      onChange={newOption => {
        if (newOption === null) {
          onChange(null);
          setSelectValue(null);
          return;
        } else if (newOption?.value) {
          onChange(newOption?.value as string);
          setSelectValue(newOption);
        }
      }}
      placeholder="All contractors"
      options={options}
      status={queryPayeeRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        (queryPayeeRows.isFetching && !queryPayeeRows.isFetchingNextPage) ||
        queryPayee.isInitialLoading
      }
      onReachMenuEnd={onReachMenuEnd}
    />
  );
};
